import { StatusBar } from "expo-status-bar";
import { Pressable, StyleSheet, ScrollView, Text, TextInput, useColorScheme, Alert } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useState } from "react";
import { View } from "../components/Themed";
import { useQuery, gql, useMutation } from "@apollo/client";
import Config from "../constants/Config";
import { v4 as uuidv4 } from "uuid";
import { Picker } from "@react-native-picker/picker";

const INVENTORY_QUERY = gql`
  query getInventory($token: String!, $category: String) {
    inventoryItems(token: $token, category: $category) {
      id
      name
      description
      unit
      supplierId
      supplier {
        id
        name
      }
      currentQuantity
      minQuantity
      category
      createdAt
      updatedAt
    }
  }
`;

const SUPPLIERS_QUERY = gql`
  query getSuppliers($token: String!) {
    suppliers(token: $token) {
      id
      name
    }
  }
`;

const CREATE_INVENTORY_ITEM_MUTATION = gql`
  mutation createInventoryItem(
    $token: String!
    $id: ID!
    $name: String!
    $description: String
    $unit: String
    $supplierId: ID
    $currentQuantity: Float!
    $minQuantity: Float
    $category: String
  ) {
    createInventoryItem(
      token: $token
      id: $id
      name: $name
      description: $description
      unit: $unit
      supplierId: $supplierId
      currentQuantity: $currentQuantity
      minQuantity: $minQuantity
      category: $category
    ) {
      id
      name
    }
  }
`;

const UPDATE_INVENTORY_ITEM_MUTATION = gql`
  mutation updateInventoryItem(
    $token: String!
    $id: ID!
    $name: String
    $description: String
    $unit: String
    $supplierId: ID
    $currentQuantity: Float
    $minQuantity: Float
    $category: String
  ) {
    updateInventoryItem(
      token: $token
      id: $id
      name: $name
      description: $description
      unit: $unit
      supplierId: $supplierId
      currentQuantity: $currentQuantity
      minQuantity: $minQuantity
      category: $category
    ) {
      id
      name
    }
  }
`;

const DELETE_INVENTORY_ITEM_MUTATION = gql`
  mutation deleteInventoryItem($token: String!, $id: ID!) {
    deleteInventoryItem(token: $token, id: $id)
  }
`;

export default function InventoryScreen() {
  const [token, setToken] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [currentQuantity, setCurrentQuantity] = useState("0");
  const [minQuantity, setMinQuantity] = useState("");
  const [category, setCategory] = useState("");
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const { loading, error, data, refetch } = useQuery(INVENTORY_QUERY, {
    variables: { token, category: filterCategory || undefined },
    skip: !token,
  });

  const { data: suppliersData } = useQuery(SUPPLIERS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [createItem] = useMutation(CREATE_INVENTORY_ITEM_MUTATION);
  const [updateItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);
  const [deleteItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);

  useEffect(() => {
    async function fetchToken() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}
    }
    fetchToken();
  }, []);

  const resetForm = () => {
    setName("");
    setDescription("");
    setUnit("");
    setSupplierId("");
    setCurrentQuantity("0");
    setMinQuantity("");
    setCategory("");
    setEditingItem(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setName(item.name || "");
    setDescription(item.description || "");
    setUnit(item.unit || "");
    setSupplierId(item.supplierId || "");
    setCurrentQuantity(String(item.currentQuantity ?? 0));
    setMinQuantity(item.minQuantity != null ? String(item.minQuantity) : "");
    setCategory(item.category || "");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert("Error", "Item name is required");
      return;
    }
    try {
      if (editingItem) {
        await updateItem({
          variables: {
            token,
            id: editingItem.id,
            name,
            description,
            unit,
            supplierId: supplierId || null,
            currentQuantity: parseFloat(currentQuantity) || 0,
            minQuantity: minQuantity ? parseFloat(minQuantity) : null,
            category,
          },
        });
      } else {
        await createItem({
          variables: {
            token,
            id: uuidv4(),
            name,
            description,
            unit,
            supplierId: supplierId || null,
            currentQuantity: parseFloat(currentQuantity) || 0,
            minQuantity: minQuantity ? parseFloat(minQuantity) : null,
            category,
          },
        });
      }
      resetForm();
      refetch();
    } catch (err) {
      Alert.alert("Error", err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteItem({ variables: { token, id } });
      refetch();
    } catch (err) {
      Alert.alert("Error", err.message);
    }
  };

  // Get unique categories from items
  const categories = [...new Set((data?.inventoryItems || []).map(i => i.category).filter(Boolean))];

  const textColor = isDark ? "#fff" : "#000";
  const cardBg = isDark ? "#1e1e1e" : "#fff";
  const inputBg = isDark ? "#2a2a2a" : "#f5f5f5";

  return (
    <ScrollView style={{ backgroundColor: isDark ? "#121212" : "#f0f0f0" }}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: textColor }]}>Inventory</Text>
          <Pressable
            style={[styles.addButton, { backgroundColor: Config.companyColor || "#007AFF" }]}
            onPress={() => { resetForm(); setShowForm(!showForm); }}
          >
            <Text style={styles.addButtonText}>{showForm ? "Cancel" : "+ Add Item"}</Text>
          </Pressable>
        </View>

        {categories.length > 0 && (
          <View style={[styles.filterRow, { backgroundColor: cardBg }]}>
            <Text style={[styles.filterLabel, { color: textColor }]}>Filter by category:</Text>
            <Picker
              selectedValue={filterCategory}
              onValueChange={setFilterCategory}
              style={[styles.picker, { color: textColor }]}
            >
              <Picker.Item label="All" value="" />
              {categories.map(c => (
                <Picker.Item key={c} label={c} value={c} />
              ))}
            </Picker>
          </View>
        )}

        {showForm && (
          <View style={[styles.formCard, { backgroundColor: cardBg }]}>
            <Text style={[styles.formTitle, { color: textColor }]}>
              {editingItem ? "Edit Item" : "New Inventory Item"}
            </Text>
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Name *"
              placeholderTextColor="#999"
              value={name}
              onChangeText={setName}
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Description"
              placeholderTextColor="#999"
              value={description}
              onChangeText={setDescription}
              multiline
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Unit (kg, pcs, L, etc.)"
              placeholderTextColor="#999"
              value={unit}
              onChangeText={setUnit}
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Current Quantity"
              placeholderTextColor="#999"
              value={currentQuantity}
              onChangeText={setCurrentQuantity}
              keyboardType="numeric"
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Min Quantity (alert threshold)"
              placeholderTextColor="#999"
              value={minQuantity}
              onChangeText={setMinQuantity}
              keyboardType="numeric"
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Category"
              placeholderTextColor="#999"
              value={category}
              onChangeText={setCategory}
            />
            {suppliersData?.suppliers?.length > 0 && (
              <View style={[styles.pickerContainer, { backgroundColor: inputBg }]}>
                <Text style={[styles.pickerLabel, { color: "#999" }]}>Supplier</Text>
                <Picker
                  selectedValue={supplierId}
                  onValueChange={setSupplierId}
                  style={{ color: textColor }}
                >
                  <Picker.Item label="None" value="" />
                  {suppliersData.suppliers.map(s => (
                    <Picker.Item key={s.id} label={s.name} value={s.id} />
                  ))}
                </Picker>
              </View>
            )}
            <Pressable style={[styles.saveButton, { backgroundColor: "#28a745" }]} onPress={handleSave}>
              <Text style={styles.saveButtonText}>{editingItem ? "Update" : "Create"}</Text>
            </Pressable>
          </View>
        )}

        {loading && <Text style={{ color: textColor }}>Loading...</Text>}
        {error && <Text style={{ color: "#dc3545" }}>Error: {error.message}</Text>}

        {data?.inventoryItems?.map((item) => {
          const isLow = item.minQuantity != null && item.currentQuantity <= item.minQuantity;
          return (
            <View key={item.id} style={[styles.itemCard, { backgroundColor: cardBg }, isLow && styles.lowStock]}>
              <View style={styles.itemHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.itemName, { color: textColor }]}>{item.name}</Text>
                  {item.category && (
                    <Text style={[styles.badge, { backgroundColor: "#e3f2fd", color: "#1565c0" }]}>{item.category}</Text>
                  )}
                </View>
                <View style={styles.quantityBox}>
                  <Text style={[styles.quantityValue, { color: isLow ? "#dc3545" : textColor }]}>
                    {item.currentQuantity}
                  </Text>
                  <Text style={[styles.quantityUnit, { color: "#888" }]}>{item.unit || "units"}</Text>
                </View>
              </View>

              {item.description && (
                <Text style={[styles.detail, { color: "#888" }]}>{item.description}</Text>
              )}
              {item.supplier && (
                <Text style={[styles.detail, { color: textColor }]}>Supplier: {item.supplier.name}</Text>
              )}
              {item.minQuantity != null && (
                <Text style={[styles.detail, { color: isLow ? "#dc3545" : "#888" }]}>
                  Min: {item.minQuantity} {item.unit || "units"} {isLow ? "⚠ LOW STOCK" : ""}
                </Text>
              )}

              <View style={styles.actions}>
                <Pressable style={styles.editButton} onPress={() => handleEdit(item)}>
                  <Text style={styles.editButtonText}>Edit</Text>
                </Pressable>
                <Pressable style={styles.deleteButton} onPress={() => handleDelete(item.id)}>
                  <Text style={styles.deleteButtonText}>Delete</Text>
                </Pressable>
              </View>
            </View>
          );
        })}

        {data?.inventoryItems?.length === 0 && (
          <Text style={[styles.emptyText, { color: "#888" }]}>No inventory items yet.</Text>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  addButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  filterLabel: {
    fontSize: 14,
    marginRight: 8,
  },
  picker: {
    flex: 1,
  },
  formCard: {
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 16,
  },
  input: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
  },
  pickerContainer: {
    borderRadius: 8,
    marginBottom: 12,
    padding: 4,
  },
  pickerLabel: {
    fontSize: 12,
    paddingHorizontal: 12,
    paddingTop: 8,
  },
  saveButton: {
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 8,
  },
  saveButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
  },
  itemCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
  },
  lowStock: {
    borderLeftWidth: 4,
    borderLeftColor: "#dc3545",
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  itemName: {
    fontSize: 17,
    fontWeight: "bold",
  },
  badge: {
    alignSelf: "flex-start",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    fontSize: 12,
    marginTop: 4,
    overflow: "hidden",
  },
  quantityBox: {
    alignItems: "center",
    paddingLeft: 16,
  },
  quantityValue: {
    fontSize: 22,
    fontWeight: "bold",
  },
  quantityUnit: {
    fontSize: 12,
  },
  detail: {
    fontSize: 14,
    marginBottom: 4,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 12,
  },
  editButton: {
    backgroundColor: "#007AFF",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  editButtonText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 13,
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  deleteButtonText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 13,
  },
  emptyText: {
    textAlign: "center",
    marginTop: 40,
    fontSize: 16,
  },
});
